/** Shared validation for Allegro offer / external ID filter fields.

 * Allegro sources (allegro_api.yaml):
 * - GET /sale/offers `external.id`: maxItems 100, items.maxLength 100
 * - schema ExternalId: maxLength 100
 *
 * Offer Repository list/ids endpoints are SQL filters — they are NOT Allegro
 * `external.id` query params, so Allegro maxItems does not apply there.
 * ExternalId maxLength still applies (values originate from Allegro).
 */
import type { TFunction } from 'i18next'

/** Allegro GET /sale/offers `external.id` maxItems (+ backend parse_ids default). */
export const ALLEGRO_EXTERNAL_IDS_MAX_COUNT = 100
/** Allegro ExternalId / `external.id` item maxLength (+ backend parse_ids default). */
export const ALLEGRO_EXTERNAL_ID_MAX_LENGTH = 100

/**
 * Soft paste ceiling for offer.id when the client chunks Allegro list requests
 * (e.g. fetchEnhancedOffers). Bundle selector does not chunk — pass Allegro list cap.
 */
export const OFFER_IDS_PASTE_MAX_WHEN_CHUNKED = 1000

export type IdValidationError =
  | { type: 'maxCount'; count: number; max: number }
  | { type: 'maxItemLen'; max: number }

export interface IdValidationResult {
  error: IdValidationError | undefined
  items: string[]
}

export function parseIds(raw: string): string[] {
  return raw.replace(/,/g, ' ').split(/\s+/).filter(Boolean)
}

export function validateOfferIds(
  raw: string,
  maxCount = OFFER_IDS_PASTE_MAX_WHEN_CHUNKED,
): IdValidationResult {
  const items = parseIds(raw)
  if (items.length > maxCount) {
    return { error: { type: 'maxCount', count: items.length, max: maxCount }, items }
  }
  return { error: undefined, items }
}

/**
 * @param maxCount Allegro list maxItems, or `null` to skip count (Offer Repository path).
 */
export function validateExternalIds(
  raw: string,
  maxCount: number | null = ALLEGRO_EXTERNAL_IDS_MAX_COUNT,
  maxItemLen = ALLEGRO_EXTERNAL_ID_MAX_LENGTH,
): IdValidationResult {
  const items = parseIds(raw)
  if (maxCount != null && items.length > maxCount) {
    return { error: { type: 'maxCount', count: items.length, max: maxCount }, items }
  }
  const tooLong = items.filter((v) => v.length > maxItemLen)
  if (tooLong.length > 0) {
    return { error: { type: 'maxItemLen', max: maxItemLen }, items }
  }
  return { error: undefined, items }
}

/** Allegro-direct list filters (selector, bundles, legacy Allegro list). */
export function validateAllegroListExternalIds(raw: string): IdValidationResult {
  return validateExternalIds(raw, ALLEGRO_EXTERNAL_IDS_MAX_COUNT, ALLEGRO_EXTERNAL_ID_MAX_LENGTH)
}

/**
 * Offer Repository `skus` filter — no Allegro maxItems; still enforce ExternalId maxLength.
 */
export function validateRepositoryExternalIds(raw: string): IdValidationResult {
  return validateExternalIds(raw, null, ALLEGRO_EXTERNAL_ID_MAX_LENGTH)
}

/** First blocking ID-filter error for Allegro-direct list/selector queries. */
export function getOfferListIdFilterError(params: {
  offerIds?: string
  externalIds?: string
  /** Cap for offer.id paste; use Allegro list cap when the client does not chunk. */
  offerIdsMaxCount?: number
}): IdValidationError | undefined {
  const offerErr = validateOfferIds(
    params.offerIds ?? '',
    params.offerIdsMaxCount ?? OFFER_IDS_PASTE_MAX_WHEN_CHUNKED,
  ).error
  if (offerErr) return offerErr
  return validateAllegroListExternalIds(params.externalIds ?? '').error
}

/** First blocking ID-filter error for Offer Repository management filters. */
export function getOfferManagementIdFilterError(params: {
  offerIds?: string
  externalIds?: string
}): IdValidationError | undefined {
  const offerErr = validateOfferIds(params.offerIds ?? '').error
  if (offerErr) return offerErr
  return validateRepositoryExternalIds(params.externalIds ?? '').error
}

/** Translate an IdValidationError to a user-facing message (allegro namespace). */
export function formatIdValidationError(
  err: IdValidationError | undefined,
  t: TFunction,
): string | undefined {
  if (!err) return undefined
  if (err.type === 'maxCount') {
    return t('allegro:offers.selector.filter.maxValues', {
      count: err.count,
      max: err.max,
      over: err.count - err.max,
    })
  }
  return t('allegro:offers.selector.filter.maxValueLength', { max: err.max })
}
